import { PlusIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import { useGetTrackedProcessesListQuery } from '../../app/apiSlice'
import { AdminDataTableSection, AdminPage, AdminPageHeader } from '../../common/components'
import { Button, Link, Modal, Table } from '../../common/designs'
import { ProcessAnalysisConfig } from '../Processes/types'
import CustomiseDimensionsModal from './CustomiseDimensionsModal'
import DataIntegrationForm from './DataIntegrationForm'
import ProcessForm from './ProcessForm'

const StructuredProcesses = () => {
  const { data } = useGetTrackedProcessesListQuery()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isCustomiseDimensionsModalOpen, setIsCustomiseDimensionsModalOpen] =
    useState<boolean>(false)
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState<boolean>(false)
  const [instance, setInstance] = useState<ProcessAnalysisConfig | null>(null)

  return (
    <AdminPage width='full'>
      <AdminPageHeader
        title='Structured Processes'
        description='Define process identifiers, dimensions, and integrations for process object tracking.'
      />
      <AdminDataTableSection>
        <Table
          label='Processes'
          description='Turn business objects into process flows here. Select the main process identifiers and slice & dice dimensions for each.'
          head={['Name', 'Process Identifier Name', 'Dimensions', 'Edit', 'Data Integrations']}
          body={(data ?? []).map((p) => [
            p.name,
            p.process_identifier_name,
            <Link
              onClick={() => {
                setInstance(p)
                setIsCustomiseDimensionsModalOpen(true)
              }}
              key={p.process_identifier_name}
            >
              Dimensions
            </Link>,
            <Link
              onClick={() => {
                setInstance(p)
                setIsEditModalOpen(true)
              }}
              key={p.process_identifier_name}
            >
              Edit
            </Link>,
            <Link
              key={p.process_identifier_name}
              onClick={() => {
                setInstance(p)
                setIsIntegrationModalOpen(true)
              }}
            >
              Data Integrations
            </Link>,
          ])}
          action={
            <Button iconStart={<PlusIcon />} onClick={() => setIsCreateModalOpen(true)}>
              Add a new process
            </Button>
          }
        />
      </AdminDataTableSection>

      <Modal
        label='Create a new process'
        size='2xl'
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      >
        <ProcessForm onClose={() => setIsCreateModalOpen(false)} />
      </Modal>

      <Modal
        label='Edit the process'
        size='2xl'
        open={isEditModalOpen && Boolean(instance)}
        onClose={() => {
          setInstance(null)
          setIsEditModalOpen(false)
        }}
      >
        <ProcessForm
          editedData={instance}
          onClose={() => {
            setInstance(null)
            setIsEditModalOpen(false)
          }}
        />
      </Modal>

      <Modal
        label='Data Integrations'
        size='4xl'
        open={isIntegrationModalOpen && Boolean(instance)}
        onClose={() => {
          setInstance(null)
          setIsIntegrationModalOpen(false)
        }}
      >
        <DataIntegrationForm key={instance?.id ?? ''} processId={instance?.id ?? ''} />
      </Modal>

      {isCustomiseDimensionsModalOpen && instance && (
        <CustomiseDimensionsModal
          isOpen={isCustomiseDimensionsModalOpen}
          onClose={() => {
            setInstance(null)
            setIsCustomiseDimensionsModalOpen(false)
          }}
          process={instance}
        />
      )}
    </AdminPage>
  )
}

export default StructuredProcesses
